/**
 * Compras — `OD-0054` (ACCEPTED 2026-10-22), plan Business para ANOTAR.
 *
 * Cuatro pestañas: las facturas recibidas, los presupuestos de proveedor
 * (`OD-0055`), lo que queda por pagar (con los pagos hechos sin factura
 * todavía, `D-05`) y los proveedores. Está en el menú
 * en todos los planes porque leer no exige el plan (`D-11`): tras bajar de
 * plan, lo anotado se sigue viendo. Lo que no se puede hacer lo explica la API
 * con `writeBlockedBy` —el plan o el rol—, y aquí solo se dice en llano.
 *
 * ## Lo que esta pantalla NO decide
 *
 * - **Ninguna cuenta de dinero** (`URS §129`): totales, pendientes y vencidos
 *   llegan sumados de la API. Por eso los filtros van a la API.
 * - **Ni qué está vencido**: los días de retraso y si el proveedor llega tarde
 *   con la factura los calcula la API con la fecha de Madrid.
 * - **Ni lo que queda de un presupuesto**: facturado, pagado, por facturar y
 *   su estado llegan de la API (`OD-0055 D-05`).
 */
import { useCallback, useEffect, useState } from "react";

import {
  apiCompras,
  apiPresupuestosProveedor,
  ESTADOS_LISTA_COMPRAS,
  ESTADOS_LISTA_PRESUPUESTOS_PROVEEDOR,
  type BloqueoCompras,
  type EstadoPresupuestoProveedor,
  type FilaCompra,
  type FilaPresupuestoProveedor,
  type FiltrosCompras,
  type FiltrosPresupuestosProveedor,
  type ListaCompras,
  type ListaPresupuestosProveedor,
  type PagosPendientesCompras,
  type ProveedorDeLista,
  type Resultado,
} from "../api/cliente";
import { t } from "../i18n/es";
import { importeLegible } from "../ui/formato";
import { BotonExportacion, PanelExportacion, useEstadoExportacion } from "./PanelExportacion";
import { Icono } from "../ui/Iconos";
import { Aviso, Boton, Campo, Casilla, FilaPulsable, Selector, Tarjeta } from "../ui/primitivas";
import { guardar, mensajeDeRechazo, type Mensaje } from "./PantallaCompra";
import { diaLegible } from "./PantallaOportunidades";

export type PestanaCompras = "recibidas" | "presupuestos" | "pendientes" | "proveedores";
const PESTANAS: readonly PestanaCompras[] = ["recibidas", "presupuestos", "pendientes", "proveedores"];

type Carga<T> = { readonly tipo: "cargando" } | { readonly tipo: "error" } | { readonly tipo: "sin-permiso" } | { readonly tipo: "datos"; readonly datos: T };

/** Por qué no se puede anotar, si no se puede. Fuera de Business, con el enlace a los planes. */
export function AvisoEscrituraCompras({ bloqueo, onVerPlan }: { bloqueo: BloqueoCompras; onVerPlan: () => void }) {
  if (bloqueo === null) return null;
  if (bloqueo === "ROLE") return <Aviso tono="info">{t.compras.soloLecturaRol}</Aviso>;
  return (
    <>
      <Aviso tono="info" titulo={t.compras.soloBusinessTitulo}>
        {t.compras.soloBusiness}
      </Aviso>
      <div className="barra-acciones">
        <Boton variante="secundario" onClick={onVerPlan}>
          {t.compras.verPlanes}
        </Boton>
      </div>
    </>
  );
}

/** El estado de pago de una compra, como en los cobros. */
export function InsigniaPago({ estado }: { estado: "UNPAID" | "PARTIALLY_PAID" | "PAID" }) {
  return <span className={`insignia insignia-pago-${estado}`}>{t.compras.estadosPago[estado]}</span>;
}

/** El estado de un presupuesto de proveedor (`OD-0055 D-05`), calculado por la API. */
export function InsigniaPresupuesto({ estado }: { estado: EstadoPresupuestoProveedor }) {
  return <span className={`insignia insignia-presupuesto-${estado}`}>{t.compras.presupuestos.estados[estado]}</span>;
}

/**
 * Una lectura de la API. Los genéricos van en su propia línea porque el
 * guardrail de textos lee línea a línea, y `>(pedir: Lectura<` le parece texto de JSX.
 */
interface Lectura<T> {
  (): Promise<Resultado<T>>;
}

function useCarga<T>(
  pedir: Lectura<T>,
  onSesionCaducada: () => void,
) {
  const [estado, setEstado] = useState<Carga<T>>({ tipo: "cargando" });
  const cargar = useCallback(async (): Promise<void> => {
    setEstado({ tipo: "cargando" });
    const r = await pedir();
    if (r.ok) return setEstado({ tipo: "datos", datos: r.datos });
    if (r.estado === 401) return onSesionCaducada();
    setEstado({ tipo: r.estado === 403 ? "sin-permiso" : "error" });
  }, [pedir, onSesionCaducada]);
  useEffect(() => {
    void cargar();
  }, [cargar]);
  return { estado, cargar };
}

function EstadoDeCarga<T>({ estado, onReintentar }: { estado: Carga<T>; onReintentar: () => void }) {
  if (estado.tipo === "cargando") return <p>{t.compras.cargando}</p>;
  if (estado.tipo === "sin-permiso") return <Aviso tono="aviso">{t.compras.sinPermiso}</Aviso>;
  if (estado.tipo === "error") {
    return (
      <>
        <Aviso tono="error">{t.compras.error}</Aviso>
        <Boton variante="secundario" onClick={onReintentar}>
          {t.compras.reintentar}
        </Boton>
      </>
    );
  }
  return null;
}

/** La lista sin filtros. Los filtros viven en el panel para que «Volver» no los pierda. */
export const SIN_FILTROS_COMPRAS: FiltrosCompras = { estado: "ALL", tipo: "ALL", fecha: "ISSUE", desde: null, hasta: null, proveedor: null, presupuesto: null, texto: "" };

export const SIN_FILTROS_PRESUPUESTOS: FiltrosPresupuestosProveedor = { estado: "ALL", desde: null, hasta: null, texto: "", tarde: false };

/** Una compra de la lista: la fila abre la ficha; al lado, descargar el adjunto y apuntar un pago. */
function FilaRecibida({
  f,
  writable,
  ocupado,
  onAbrir,
  onDescargar,
  onApuntarPago,
  onAbrirPresupuesto,
}: {
  f: FilaCompra;
  writable: boolean;
  ocupado: boolean;
  onAbrir: () => void;
  onDescargar: () => void;
  onApuntarPago: () => void;
  onAbrirPresupuesto: () => void;
}) {
  const puedePagar = writable && f.status === "REGISTERED" && f.paymentStatus !== "PAID";
  return (
    <li className="fila fila-con-acciones">
      <button type="button" className="fila-pulsable" onClick={onAbrir}>
        <span className="fila-texto">
          <span className={f.status === "VOIDED" ? "fila-titulo cobro-anulado" : "fila-titulo"}>
            {f.registryNumber} · {f.supplierName}
          </span>
          <span className="ayuda-neutra">
            {t.compras.suFactura(f.supplierInvoiceNumber)} · {diaLegible(f.issueDate)}
            {f.kind === "ADVANCE" ? ` · ${t.compras.tipos.ADVANCE}` : ""}
            {f.quoteNumber !== null ? ` · ${t.compras.dePresupuesto(f.quoteNumber)}` : ""}
            {f.reference !== null ? ` · ${f.reference}` : ""}
          </span>
          <span className="ayuda-neutra">
            {f.status === "VOIDED" ? (
              <span className="insignia">{t.compras.anulada}</span>
            ) : (
              <>
                <InsigniaPago estado={f.paymentStatus} />
                {f.overdue ? (
                  <>
                    {" "}
                    <span className="insignia insignia-vencida">{t.compras.vencida}</span>
                  </>
                ) : null}{" "}
                · {t.compras.deTotal(importeLegible(f.pending), importeLegible(f.payable))}
                {f.dueDate !== null ? ` · ${t.compras.vence(diaLegible(f.dueDate))}` : ""}
              </>
            )}
          </span>
        </span>
        <span className="fila-flecha" aria-hidden="true">
          <Icono nombre="siguiente" />
        </span>
      </button>
      {f.hasAttachment || puedePagar || f.supplierQuoteId !== null ? (
        <div className="fila-acciones" role="group" aria-label={t.compras.accionesDe(f.registryNumber)}>
          {f.supplierQuoteId !== null ? (
            <Boton variante="terciario" onClick={onAbrirPresupuesto}>
              {t.compras.verPresupuesto}
            </Boton>
          ) : null}
          {f.hasAttachment ? (
            <Boton variante="terciario" onClick={onDescargar} deshabilitado={ocupado}>
              {t.compras.descargarFila}
            </Boton>
          ) : null}
          {puedePagar ? (
            <Boton variante="secundario" onClick={onApuntarPago}>
              {t.compras.apuntarPagoFila}
            </Boton>
          ) : null}
        </div>
      ) : null}
    </li>
  );
}

function Recibidas({
  filtros,
  onFiltros,
  onAbrir,
  onApuntarPago,
  onAbrirPresupuesto,
  onNueva,
  onVerPlan,
  onSesionCaducada,
  hayConexion,
}: {
  filtros: FiltrosCompras;
  onFiltros: (f: FiltrosCompras) => void;
  onAbrir: (id: string) => void;
  onApuntarPago: (id: string) => void;
  onAbrirPresupuesto: (id: string) => void;
  onNueva: () => void;
  onVerPlan: () => void;
  onSesionCaducada: () => void;
  hayConexion: boolean;
}) {
  // OD-0054-ENMIENDA-01: aquí solo las de proveedores, y solo si el rol las ve.
  const exportacion = useEstadoExportacion();
  const conExportacion = exportacion !== null && exportacion.veCompras;
  const [exportacionAbierta, setExportacionAbierta] = useState(false);
  // El texto va a la API, pero no con cada tecla.
  const [texto, setTexto] = useState(filtros.texto);
  useEffect(() => {
    if (texto === filtros.texto) return;
    const h = setTimeout(() => onFiltros({ ...filtros, texto }), 300);
    return () => clearTimeout(h);
  }, [texto, filtros, onFiltros]);
  const pedir = useCallback(() => apiCompras.listar(filtros), [filtros]);
  const { estado, cargar } = useCarga<ListaCompras>(pedir, onSesionCaducada);
  const datos = estado.tipo === "datos" ? estado.datos : null;
  const hayFiltros =
    filtros.estado !== "ALL" ||
    filtros.tipo !== "ALL" ||
    filtros.fecha !== "ISSUE" ||
    filtros.desde !== null ||
    filtros.hasta !== null ||
    filtros.presupuesto !== null ||
    filtros.texto.trim() !== "";
  const [descargando, setDescargando] = useState<string | null>(null);
  const [mensaje, setMensaje] = useState<Mensaje | null>(null);

  async function descargar(id: string): Promise<void> {
    setDescargando(id);
    setMensaje(null);
    const r = await apiCompras.descargarAdjunto(id);
    setDescargando(null);
    if (r.ok) return guardar(r.datos.contenido, r.datos.nombre);
    if (r.estado === 401) return onSesionCaducada();
    setMensaje(mensajeDeRechazo(r));
  }

  return (
    <>
      {datos !== null ? <AvisoEscrituraCompras bloqueo={datos.bloqueo} onVerPlan={onVerPlan} /> : null}
      {filtros.presupuesto !== null ? (
        <Aviso tono="info">
          {t.compras.soloDelPresupuesto(filtros.presupuesto.numero)}{" "}
          <button type="button" className="enlace-en-texto" onClick={() => onFiltros({ ...filtros, presupuesto: null })}>
            {t.compras.verTodas}
          </button>
        </Aviso>
      ) : null}
      <div className="barra-acciones">
        <div className="fichas" role="group" aria-label={t.compras.filtroEstado}>
          {ESTADOS_LISTA_COMPRAS.map((e) => (
            <button key={e} type="button" className="ficha" aria-pressed={filtros.estado === e} onClick={() => onFiltros({ ...filtros, estado: e })}>
              <span>{t.compras.filtros[e]}</span>
            </button>
          ))}
        </div>
        <div className="barra-acciones-botones">
          {conExportacion ? <BotonExportacion abierto={exportacionAbierta} onClick={() => setExportacionAbierta((v) => !v)} /> : null}
          {datos?.writable === true ? <Boton onClick={onNueva}>{t.compras.anotar}</Boton> : null}
        </div>
      </div>
      {conExportacion && exportacionAbierta ? (
        <PanelExportacion estado={exportacion} hayConexion={hayConexion} alcance="compras" onCerrar={() => setExportacionAbierta(false)} />
      ) : null}
      <Campo id="compras-buscar" etiqueta={t.compras.buscarCompra} valor={texto} onChange={setTexto} requerido={false} />
      <div className="pendientes-filtros compras-filtros">
        <Selector
          id="compras-tipo"
          etiqueta={t.compras.filtroTipo}
          valor={filtros.tipo}
          opciones={(["ALL", "ORDINARY", "ADVANCE"] as const).map((v) => ({ valor: v, texto: t.compras.tiposFiltro[v] }))}
          onChange={(v) => onFiltros({ ...filtros, tipo: v })}
        />
        <Selector
          id="compras-fecha"
          etiqueta={t.compras.filtroFecha}
          valor={filtros.fecha}
          opciones={(["ISSUE", "DUE"] as const).map((v) => ({ valor: v, texto: t.compras.fechasFiltro[v] }))}
          onChange={(v) => onFiltros({ ...filtros, fecha: v })}
        />
        <Campo
          id="compras-desde"
          etiqueta={t.compras.desde}
          tipo="date"
          requerido={false}
          valor={filtros.desde ?? ""}
          onChange={(v) => onFiltros({ ...filtros, desde: v === "" ? null : v })}
        />
        <Campo
          id="compras-hasta"
          etiqueta={t.compras.hasta}
          tipo="date"
          requerido={false}
          valor={filtros.hasta ?? ""}
          onChange={(v) => onFiltros({ ...filtros, hasta: v === "" ? null : v })}
        />
        {hayFiltros ? (
          <Boton
            variante="secundario"
            onClick={() => {
              setTexto("");
              onFiltros(SIN_FILTROS_COMPRAS);
            }}
          >
            {t.compras.limpiar}
          </Boton>
        ) : null}
      </div>

      <EstadoDeCarga estado={estado} onReintentar={() => void cargar()} />
      {mensaje !== null ? <Aviso tono={mensaje.tono}>{mensaje.texto}</Aviso> : null}
      {datos !== null && datos.filas.length > 0 ? (
        <p className="ayuda-neutra">{t.compras.resumenLista(importeLegible(datos.total), importeLegible(datos.pendiente))}</p>
      ) : null}
      {datos !== null && datos.filas.length === 0 ? (
        hayFiltros ? (
          <Aviso tono="info">{t.compras.vacioFiltro}</Aviso>
        ) : (
          <div className="estado-vacio">
            <span className="estado-vacio-icono" aria-hidden="true">
              <Icono nombre="compras" />
            </span>
            <p className="estado-vacio-titulo">{t.compras.vacioTitulo}</p>
            <p>{t.compras.vacio}</p>
          </div>
        )
      ) : null}
      {datos !== null && datos.filas.length > 0 ? (
        <ul className="lista-filas">
          {datos.filas.map((f) => (
            <FilaRecibida
              key={f.id}
              f={f}
              writable={datos.writable}
              ocupado={descargando === f.id}
              onAbrir={() => onAbrir(f.id)}
              onDescargar={() => void descargar(f.id)}
              onApuntarPago={() => onApuntarPago(f.id)}
              onAbrirPresupuesto={() => onAbrirPresupuesto(f.supplierQuoteId ?? "")}
            />
          ))}
        </ul>
      ) : null}
    </>
  );
}

/** Un presupuesto de la lista: la fila abre su ficha; al lado, descargar y anotar una factura de él. */
function FilaPresupuesto({
  f,
  writable,
  ocupado,
  onAbrir,
  onDescargar,
  onAnotarFactura,
}: {
  f: FilaPresupuestoProveedor;
  writable: boolean;
  ocupado: boolean;
  onAbrir: () => void;
  onDescargar: () => void;
  onAnotarFactura: () => void;
}) {
  // Se le enlazan facturas aceptado y sin terminar; la API lo vuelve a comprobar (`D-04`).
  const puedeFacturar = writable && (f.state === "ACCEPTED" || f.state === "PARTIALLY_INVOICED");
  return (
    <li className="fila fila-con-acciones">
      <button type="button" className="fila-pulsable" onClick={onAbrir}>
        <span className="fila-texto">
          <span className={f.state === "CANCELLED" ? "fila-titulo cobro-anulado" : "fila-titulo"}>
            {f.quoteNumber} · {f.supplierName}
          </span>
          <span className="ayuda-neutra">
            {f.supplierQuoteNumber !== null ? `${t.compras.presupuestos.suNumero(f.supplierQuoteNumber)} · ` : ""}
            {diaLegible(f.issueDate)}
            {f.reference !== null ? ` · ${f.reference}` : ""}
          </span>
          <span className="ayuda-neutra">
            <InsigniaPresupuesto estado={f.state} /> · {t.compras.presupuestos.resumenFila(importeLegible(f.total), importeLegible(f.paid), importeLegible(f.toInvoice))}
            {f.lateUnbilledPayments > 0 ? (
              <>
                {" · "}
                <strong className="pendientes-retraso">{t.compras.presupuestos.pagosTarde(f.lateUnbilledPayments)}</strong>
              </>
            ) : null}
          </span>
        </span>
        <span className="fila-flecha" aria-hidden="true">
          <Icono nombre="siguiente" />
        </span>
      </button>
      {f.hasAttachment || puedeFacturar ? (
        <div className="fila-acciones" role="group" aria-label={t.compras.presupuestos.accionesDe(f.quoteNumber)}>
          {f.hasAttachment ? (
            <Boton variante="terciario" onClick={onDescargar} deshabilitado={ocupado}>
              {t.compras.descargarFila}
            </Boton>
          ) : null}
          {puedeFacturar ? (
            <Boton variante="secundario" onClick={onAnotarFactura}>
              {t.compras.presupuestos.anotarFactura}
            </Boton>
          ) : null}
        </div>
      ) : null}
    </li>
  );
}

function Presupuestos({
  filtros,
  onFiltros,
  onAbrir,
  onNuevo,
  onAnotarFacturaDe,
  onVerPlan,
  onSesionCaducada,
}: {
  filtros: FiltrosPresupuestosProveedor;
  onFiltros: (f: FiltrosPresupuestosProveedor) => void;
  onAbrir: (id: string) => void;
  onNuevo: () => void;
  onAnotarFacturaDe: (f: FilaPresupuestoProveedor) => void;
  onVerPlan: () => void;
  onSesionCaducada: () => void;
}) {
  // El texto va a la API, pero no con cada tecla.
  const [texto, setTexto] = useState(filtros.texto);
  useEffect(() => {
    if (texto === filtros.texto) return;
    const h = setTimeout(() => onFiltros({ ...filtros, texto }), 300);
    return () => clearTimeout(h);
  }, [texto, filtros, onFiltros]);
  const pedir = useCallback(() => apiPresupuestosProveedor.listar(filtros), [filtros]);
  const { estado, cargar } = useCarga<ListaPresupuestosProveedor>(pedir, onSesionCaducada);
  const datos = estado.tipo === "datos" ? estado.datos : null;
  const hayFiltros = filtros.estado !== "ALL" || filtros.desde !== null || filtros.hasta !== null || filtros.tarde || filtros.texto.trim() !== "";
  const [descargando, setDescargando] = useState<string | null>(null);
  const [mensaje, setMensaje] = useState<Mensaje | null>(null);

  async function descargar(id: string): Promise<void> {
    setDescargando(id);
    setMensaje(null);
    const r = await apiPresupuestosProveedor.descargarAdjunto(id);
    setDescargando(null);
    if (r.ok) return guardar(r.datos.contenido, r.datos.nombre);
    if (r.estado === 401) return onSesionCaducada();
    setMensaje(mensajeDeRechazo(r));
  }

  return (
    <>
      {datos !== null ? <AvisoEscrituraCompras bloqueo={datos.bloqueo} onVerPlan={onVerPlan} /> : null}
      <div className="barra-acciones">
        <div className="fichas" role="group" aria-label={t.compras.presupuestos.filtroEstado}>
          {ESTADOS_LISTA_PRESUPUESTOS_PROVEEDOR.map((e) => (
            <button key={e} type="button" className="ficha" aria-pressed={filtros.estado === e} onClick={() => onFiltros({ ...filtros, estado: e })}>
              <span>{t.compras.presupuestos.filtros[e]}</span>
            </button>
          ))}
        </div>
        {datos?.writable === true ? <Boton onClick={onNuevo}>{t.compras.presupuestos.anotar}</Boton> : null}
      </div>
      <Campo id="presupuestos-proveedor-buscar" etiqueta={t.compras.presupuestos.buscar} valor={texto} onChange={setTexto} requerido={false} />
      <div className="pendientes-filtros compras-filtros">
        <Campo
          id="presupuestos-proveedor-desde"
          etiqueta={t.compras.desde}
          tipo="date"
          requerido={false}
          valor={filtros.desde ?? ""}
          onChange={(v) => onFiltros({ ...filtros, desde: v === "" ? null : v })}
        />
        <Campo
          id="presupuestos-proveedor-hasta"
          etiqueta={t.compras.hasta}
          tipo="date"
          requerido={false}
          valor={filtros.hasta ?? ""}
          onChange={(v) => onFiltros({ ...filtros, hasta: v === "" ? null : v })}
        />
        {hayFiltros ? (
          <Boton
            variante="secundario"
            onClick={() => {
              setTexto("");
              onFiltros(SIN_FILTROS_PRESUPUESTOS);
            }}
          >
            {t.compras.limpiar}
          </Boton>
        ) : null}
      </div>
      <Casilla id="presupuestos-proveedor-tarde" etiqueta={t.compras.presupuestos.tarde} marcada={filtros.tarde} onChange={(v) => onFiltros({ ...filtros, tarde: v })} />

      <EstadoDeCarga estado={estado} onReintentar={() => void cargar()} />
      {mensaje !== null ? <Aviso tono={mensaje.tono}>{mensaje.texto}</Aviso> : null}
      {datos !== null && datos.filas.length === 0 ? (
        hayFiltros ? (
          <Aviso tono="info">{t.compras.presupuestos.vacioFiltro}</Aviso>
        ) : (
          <div className="estado-vacio">
            <span className="estado-vacio-icono" aria-hidden="true">
              <Icono nombre="compras" />
            </span>
            <p className="estado-vacio-titulo">{t.compras.presupuestos.vacioTitulo}</p>
            <p>{t.compras.presupuestos.vacio}</p>
          </div>
        )
      ) : null}
      {datos !== null && datos.filas.length > 0 ? (
        <ul className="lista-filas">
          {datos.filas.map((f) => (
            <FilaPresupuesto
              key={f.id}
              f={f}
              writable={datos.writable}
              ocupado={descargando === f.id}
              onAbrir={() => onAbrir(f.id)}
              onDescargar={() => void descargar(f.id)}
              onAnotarFactura={() => onAnotarFacturaDe(f)}
            />
          ))}
        </ul>
      ) : null}
    </>
  );
}

function Pendientes({
  onAbrir,
  onAbrirProveedor,
  onVerPlan,
  onSesionCaducada,
}: {
  onAbrir: (id: string) => void;
  onAbrirProveedor: (id: string) => void;
  onVerPlan: () => void;
  onSesionCaducada: () => void;
}) {
  const pedir = useCallback(() => apiCompras.pagosPendientes(), []);
  const { estado, cargar } = useCarga<PagosPendientesCompras>(pedir, onSesionCaducada);
  const datos = estado.tipo === "datos" ? estado.datos : null;
  return (
    <>
      {datos !== null ? <AvisoEscrituraCompras bloqueo={datos.bloqueo} onVerPlan={onVerPlan} /> : null}
      <EstadoDeCarga estado={estado} onReintentar={() => void cargar()} />
      {datos !== null ? (
        <p className="ayuda-neutra">
          {t.compras.aFecha(diaLegible(datos.aFecha))} · {t.compras.totalPendiente(importeLegible(datos.pendiente), importeLegible(datos.vencido))}
        </p>
      ) : null}

      {datos !== null && datos.sinFactura.length > 0 ? (
        <section aria-label={t.compras.sinFacturaTitulo}>
          <h3 className="subtitulo">{t.compras.sinFacturaTitulo}</h3>
          <p className="ayuda-neutra">{t.compras.sinFacturaAyuda}</p>
          <ul className="lista-filas">
            {datos.sinFactura.map((p) => (
              <FilaPulsable key={p.id} onClick={() => onAbrirProveedor(p.supplierId)}>
                <span className="fila-titulo">
                  {importeLegible(p.amount)} € · {p.supplierName}
                </span>
                <span className="ayuda-neutra">
                  {t.compras.pagadoEl(diaLegible(p.paidOn))}
                  {p.late ? (
                    <>
                      {" · "}
                      <strong className="pendientes-retraso">{t.compras.facturaTarde}</strong>
                    </>
                  ) : (
                    ` · ${t.compras.facturaAntesDe(diaLegible(p.invoiceDueBy))}`
                  )}
                </span>
              </FilaPulsable>
            ))}
          </ul>
        </section>
      ) : null}

      {datos !== null && datos.filas.length === 0 ? <Aviso tono="info">{t.compras.nadaPendiente}</Aviso> : null}
      {datos !== null && datos.filas.length > 0 ? (
        <section aria-label={t.compras.porPagarTitulo}>
          <h3 className="subtitulo">{t.compras.porPagarTitulo}</h3>
          <ul className="lista-filas">
            {datos.filas.map((f) => (
              <FilaPulsable key={f.id} onClick={() => onAbrir(f.id)}>
                <span className="fila-titulo">
                  {f.supplierName} · {t.compras.suFactura(f.supplierInvoiceNumber)}
                </span>
                <span className="ayuda-neutra">
                  <InsigniaPago estado={f.paymentStatus} /> · {t.compras.deTotal(importeLegible(f.pending), importeLegible(f.payable))}
                </span>
                <span className="ayuda-neutra">
                  {f.dueDate !== null ? t.compras.vence(diaLegible(f.dueDate)) : t.compras.sinVencimiento}
                  {f.daysOverdue !== null && f.daysOverdue > 0 ? (
                    <>
                      {" · "}
                      <strong className="pendientes-retraso">{t.compras.retraso(f.daysOverdue)}</strong>
                    </>
                  ) : null}
                </span>
              </FilaPulsable>
            ))}
          </ul>
        </section>
      ) : null}
    </>
  );
}

function Proveedores({
  onAbrir,
  onNuevo,
  onVerPlan,
  onSesionCaducada,
}: {
  onAbrir: (id: string) => void;
  onNuevo: () => void;
  onVerPlan: () => void;
  onSesionCaducada: () => void;
}) {
  const [filtro, setFiltro] = useState<"ACTIVE" | "ARCHIVED">("ACTIVE");
  const [texto, setTexto] = useState("");
  const [buscado, setBuscado] = useState("");
  // La búsqueda va a la API, pero no con cada tecla.
  useEffect(() => {
    const h = setTimeout(() => setBuscado(texto), 300);
    return () => clearTimeout(h);
  }, [texto]);
  const pedir = useCallback(() => apiCompras.listarProveedores(filtro, buscado), [filtro, buscado]);
  const { estado, cargar } = useCarga<{ readonly proveedores: readonly ProveedorDeLista[]; readonly writable: boolean; readonly bloqueo: BloqueoCompras }>(
    pedir,
    onSesionCaducada,
  );
  const datos = estado.tipo === "datos" ? estado.datos : null;
  return (
    <>
      {datos !== null ? <AvisoEscrituraCompras bloqueo={datos.bloqueo} onVerPlan={onVerPlan} /> : null}
      <div className="barra-acciones">
        <div className="segmentado" role="group" aria-label={t.compras.filtroProveedores}>
          {(["ACTIVE", "ARCHIVED"] as const).map((f) => (
            <button key={f} type="button" className="menu-opcion" aria-pressed={filtro === f} onClick={() => setFiltro(f)}>
              {f === "ACTIVE" ? t.compras.activos : t.compras.archivados}
            </button>
          ))}
        </div>
        {datos?.writable === true ? <Boton onClick={onNuevo}>{t.compras.nuevoProveedor}</Boton> : null}
      </div>
      <Campo id="proveedores-buscar" etiqueta={t.compras.buscarProveedor} valor={texto} onChange={setTexto} requerido={false} />
      <EstadoDeCarga estado={estado} onReintentar={() => void cargar()} />
      {datos !== null && datos.proveedores.length === 0 ? (
        <Aviso tono="info">{buscado !== "" ? t.compras.sinCoincidencias : filtro === "ACTIVE" ? t.compras.sinProveedores : t.compras.sinArchivados}</Aviso>
      ) : null}
      {datos !== null && datos.proveedores.length > 0 ? (
        <ul className="lista-filas">
          {datos.proveedores.map((p) => (
            <FilaPulsable key={p.id} onClick={() => onAbrir(p.id)}>
              <span className="fila-titulo">{p.name}</span>
              <span className="ayuda-neutra">
                {p.taxId} · {t.compras.pendienteDe(importeLegible(p.pending))}
                {p.unbilledPayments > 0 ? ` · ${t.compras.pagosSinFactura(p.unbilledPayments)}` : ""}
              </span>
            </FilaPulsable>
          ))}
        </ul>
      ) : null}
    </>
  );
}

export function PantallaCompras({
  pestana,
  onPestana,
  filtros,
  onFiltros,
  onAbrirCompra,
  onApuntarPago,
  onNuevaCompra,
  filtrosPresupuestos,
  onFiltrosPresupuestos,
  onAbrirPresupuesto,
  onNuevoPresupuesto,
  onAnotarFacturaDe,
  onAbrirProveedor,
  onNuevoProveedor,
  onVerPlan,
  onSesionCaducada,
  hayConexion,
}: {
  pestana: PestanaCompras;
  onPestana: (p: PestanaCompras) => void;
  filtros: FiltrosCompras;
  onFiltros: (f: FiltrosCompras) => void;
  onAbrirCompra: (id: string) => void;
  onApuntarPago: (id: string) => void;
  onNuevaCompra: () => void;
  filtrosPresupuestos: FiltrosPresupuestosProveedor;
  onFiltrosPresupuestos: (f: FiltrosPresupuestosProveedor) => void;
  onAbrirPresupuesto: (id: string) => void;
  onNuevoPresupuesto: () => void;
  /** «Anotar factura» de un presupuesto: la compra nace con él elegido (`D-04`). */
  onAnotarFacturaDe: (f: FilaPresupuestoProveedor) => void;
  onAbrirProveedor: (id: string) => void;
  onNuevoProveedor: () => void;
  onVerPlan: () => void;
  onSesionCaducada: () => void;
  /** Para la exportación de recibidas: sin red no se descarga. */
  hayConexion: boolean;
}) {
  return (
    <Tarjeta titulo={t.compras.titulo}>
      <div className="pestanas" role="group" aria-label={t.compras.secciones}>
        {PESTANAS.map((p) => (
          <button key={p} type="button" className="pestana" aria-pressed={pestana === p} onClick={() => onPestana(p)}>
            <span>{t.compras.pestanas[p]}</span>
          </button>
        ))}
      </div>
      {pestana === "recibidas" ? (
        <Recibidas
          filtros={filtros}
          onFiltros={onFiltros}
          onAbrir={onAbrirCompra}
          onApuntarPago={onApuntarPago}
          onAbrirPresupuesto={onAbrirPresupuesto}
          onNueva={onNuevaCompra}
          onVerPlan={onVerPlan}
          onSesionCaducada={onSesionCaducada}
          hayConexion={hayConexion}
        />
      ) : null}
      {pestana === "presupuestos" ? (
        <Presupuestos
          filtros={filtrosPresupuestos}
          onFiltros={onFiltrosPresupuestos}
          onAbrir={onAbrirPresupuesto}
          onNuevo={onNuevoPresupuesto}
          onAnotarFacturaDe={onAnotarFacturaDe}
          onVerPlan={onVerPlan}
          onSesionCaducada={onSesionCaducada}
        />
      ) : null}
      {pestana === "pendientes" ? (
        <Pendientes onAbrir={onAbrirCompra} onAbrirProveedor={onAbrirProveedor} onVerPlan={onVerPlan} onSesionCaducada={onSesionCaducada} />
      ) : null}
      {pestana === "proveedores" ? (
        <Proveedores onAbrir={onAbrirProveedor} onNuevo={onNuevoProveedor} onVerPlan={onVerPlan} onSesionCaducada={onSesionCaducada} />
      ) : null}
    </Tarjeta>
  );
}
